import React, { FC, Suspense, useRef, useEffect } from 'react'
import Box from 'components/Box'
import styled from 'styled-components'

import { GetActivityFeedQuery } from 'gqlTypes'

import Spinner from 'components/Spinner'
import ActivityListEmptyState from '../ActivityListEmptyState'
import lazyWithPreload from 'utils/lazyWithPreload'
import { Activities } from './Activities'
import { ArtboardInSidebar, ShareInSidebar } from 'modules/shares/types'

const Comment = lazyWithPreload(() => import('components/Comment') as any)

/**
 * Styles
 */
const StyledSpinner = styled(Spinner)`
  margin: auto;
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
`

const ActivitiesLoadingIndicator = () => (
  <StyledSpinner primary data-testid="activities-loading-indicator" />
)

const ScrollContainer = styled(Box)`
  height: 100%;
  width: 100%;
  overflow: auto;
  /* Force GPU acceleration for Safari */
  transform: translateZ(0);
`

/**
 * Types
 */
interface ActivityListProps {
  artboard: ArtboardInSidebar
  share: ShareInSidebar
  isLoading: boolean
  onLoadMore: () => Promise<void>
  activities: GetActivityFeedQuery['activityFeed']['entries']
  activityToShow: string
}

/**
 * Component
 */
export const ActivityList: FC<ActivityListProps> = ({
  artboard,
  isLoading,
  onLoadMore,
  activities,
  share,
  activityToShow,
}) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const commentsEndRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    Comment.preload().then(scrollToBottom)
  }, [])

  const scrollToBottom = () => {
    window.requestAnimationFrame(() => {
      if (commentsEndRef.current) {
        commentsEndRef.current.scrollIntoView()
      }
    })
  }

  const getScrollInfo = () => {
    const scrollContainer = scrollContainerRef.current

    // getSnapshotBeforeUpdate() in activities was replaced by an equivalent
    // solution using hooks.
    // Because of this, closing the Artboard page and reopening again, will
    // cause scrollContainerRef to become "null"
    if (!scrollContainer) {
      return {
        scrollPosition: 0,
        scrollAtBottom: false,
      }
    }

    const scrollPosition =
      scrollContainer.scrollHeight - scrollContainer.scrollTop

    const scrollAtBottom =
      scrollContainer.scrollHeight - scrollContainer.scrollTop ===
      scrollContainer.clientHeight

    return {
      scrollPosition,
      scrollAtBottom,
    }
  }

  const setScrollTop = (scrollPosition: number) => {
    const scrollContainer = scrollContainerRef.current

    if (scrollContainer) {
      scrollContainer.scrollTop = scrollContainer.scrollHeight - scrollPosition
    }
  }

  async function loadMore() {
    const container = scrollContainerRef.current

    if (!container) return onLoadMore()

    // Avoiding to loose the scroll position, when new items are prepended
    const height = container.scrollHeight

    await onLoadMore()

    // if the scrollTop is 0, and we prepend items into a div,
    // the browser will remains at scrollTop 0, and this means that the
    // elements currently visible by the user will be pushed down by the new ones
    // This does not happen when the scrollTop is not 0
    if (container.scrollTop < 1)
      container.scrollTop = container.scrollHeight - height
  }
  return (
    <ScrollContainer
      ref={scrollContainerRef}
      data-testid="activity-list-scroller">
      <Suspense fallback={<ActivitiesLoadingIndicator />}>
        {isLoading && activities.length <= 0 ? (
          <ActivitiesLoadingIndicator />
        ) : activities.length > 0 ? (
          <Activities
            activities={activities}
            onLoadMore={loadMore}
            scrollToBottom={scrollToBottom}
            artboard={artboard}
            shareIdentifier={share.identifier}
            getScrollInfo={getScrollInfo}
            setScrollTop={setScrollTop}
            userCanAccessVersions={share.userCanAccessVersions}
            activityToShow={activityToShow}
          />
        ) : (
          <ActivityListEmptyState hasCommentsEnabled={share.commentsEnabled} />
        )}
      </Suspense>
      <div style={{ float: 'left', clear: 'both' }} ref={commentsEndRef} />
    </ScrollContainer>
  )
}
